/*
 * The frame of every signed-out page that is not the sign-in itself: forgot password, reset password
 * and accept an invitation (docs/2026-10-19-account-email-and-auth-flows-plan.md, section 9). It is the
 * sign-in's own frame (the wordmark on the paper, one column, the form) without the Moment: no example
 * reply, no Ronzino greeting and no arrival. These pages are tasks, so they speak in Working Mode.
 */
import { type ReactNode, useEffect, useId } from "react";
import { PASSWORD_MIN } from "./account-copy.ts";
import { DarkThemeContext, useTheme } from "./theme.ts";

export function AuthFrame(props: {
  title: string;
  tab: string;
  sub?: ReactNode;
  children: ReactNode;
}) {
  const { dark } = useTheme();
  useEffect(() => {
    document.title = `${props.tab} · LeadCue`;
  }, [props.tab]);
  return (
    <DarkThemeContext.Provider value={dark}>
      <div id="leadcue-reply-rep-preview" data-theme={dark ? "dark" : "light"}>
        <div className="lcr-app">
          <div className="lcr-body">
            <main className="lcr-signin lcr-auth-page">
              <a className="lcr-auth-home" href="/">
                <img
                  className="lcr-signin-mark"
                  src={dark ? "/leadcue-logo-white.svg" : "/leadcue-logo-dark.svg"}
                  alt="LeadCue"
                />
              </a>
              <div className="lcr-signin-col">
                <h1 className="lcr-auth-heading">{props.title}</h1>
                {props.sub && <div className="lcr-signin-sub">{props.sub}</div>}
                {props.children}
              </div>
            </main>
          </div>
        </div>
      </div>
    </DarkThemeContext.Provider>
  );
}

/** One answer of a JSON route: its status, its parsed body (or null) and any wait it asked for. */
export type RouteAnswer = {
  readonly status: number;
  readonly body: unknown;
  readonly retryAfter: string | null;
};

/** A POST from a signed-out page. A failed network is status 0, never an exception. */
export async function postJson(path: string, body: object): Promise<RouteAnswer> {
  try {
    const response = await fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    return {
      status: response.status,
      body: await response.json().catch(() => null),
      retryAfter: response.headers.get("retry-after"),
    };
  } catch {
    return { status: 0, body: null, retryAfter: null };
  }
}

/** The `error` code of a refused answer, or null when it has none. */
export function errorCode(body: unknown): string | null {
  if (typeof body !== "object" || body === null || !("error" in body)) return null;
  return typeof body.error === "string" ? body.error : null;
}

/** A new password and its confirmation, with the rule said before anything is typed. */
export function NewPasswordFields(props: {
  password: string;
  confirm: string;
  onPassword: (value: string) => void;
  onConfirm: (value: string) => void;
  invalid: boolean;
  describedBy?: string | undefined;
  label?: string;
}) {
  const id = useId();
  const hint = `${id}-hint`;
  const described = [hint, props.describedBy].filter(Boolean).join(" ");
  return (
    <>
      <div className="lcr-signin-field">
        <label htmlFor={`${id}-new`}>{props.label ?? "New password"}</label>
        <input
          id={`${id}-new`}
          type="password"
          autoComplete="new-password"
          required
          minLength={PASSWORD_MIN}
          value={props.password}
          aria-invalid={props.invalid || undefined}
          aria-describedby={described}
          onChange={(event) => props.onPassword(event.target.value)}
        />
        <span id={hint} className="lcr-auth-hint">
          At least {PASSWORD_MIN} characters.
        </span>
      </div>
      <div className="lcr-signin-field">
        <label htmlFor={`${id}-confirm`}>Type it again</label>
        <input
          id={`${id}-confirm`}
          type="password"
          autoComplete="new-password"
          required
          value={props.confirm}
          aria-invalid={props.invalid || undefined}
          aria-describedby={props.describedBy}
          onChange={(event) => props.onConfirm(event.target.value)}
        />
      </div>
    </>
  );
}
